// ── LeadsByMonthPage ("Leads by Month") — lead volume pivot + detail ──
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, STh, SectionHeader, Spinner, Td, TdNum } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { PivotMatrix, parseDate, isExcludedOwner, OwnerMultiSelect } from "./analyticsShared";

const reqAmt = (x) => parseFloat(x.requested_amount || 0);

export const LeadsByMonthPage = () => {
  const { api } = useApp();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [created, setCreated] = useState({ from: "", to: "" });
  const [company, setCompany] = useState("");
  const [sourceSel, setSourceSel] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);   // Set multi-select; null = default (all except excluded four)
  const [statusSel, setStatusSel] = useState("");
  const [empSel, setEmpSel] = useState("");

  const load = () => {
    setLoading(true);
    api.get("getLeads")
      .then(l => { setLeads(Array.isArray(l) ? l : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const owners = useMemo(() => [...new Set(leads.map(l => l.ownerName).filter(Boolean))].sort(), [leads]);
  const sources = useMemo(() => [...new Set(leads.map(l => l.lead_source).filter(Boolean))].sort(), [leads]);
  const statuses = useMemo(() => [...new Set(leads.map(l => l.status).filter(Boolean))].sort(), [leads]);

  // default Sourced By to every owner except the excluded four
  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  // Unassigned (no owner) leads are always shown so they can be addressed.
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  const rows = useMemo(() => {
    const c = company.trim().toLowerCase();
    const fromD = created.from ? new Date(created.from) : null;
    const toD = created.to ? new Date(created.to) : null;
    return leads.filter(l => {
      if (!ownerOk(l.ownerName)) return false;
      if (c && !((l.companyname || "").toLowerCase().includes(c))) return false;
      if (sourceSel && l.lead_source !== sourceSel) return false;
      if (statusSel && l.status !== statusSel) return false;
      if (fromD || toD) {
        const d = parseDate(l.createdat);
        if (!d) return false;
        if (fromD && d < fromD) return false;
        if (toD && d > toD) return false;
      }
      return true;
    });
  }, [leads, company, sourceSel, ownerSel, statusSel, created]);

  // empSel highlights the matrix (which keeps every employee) but filters the detail
  const rowsSel = useMemo(() => empSel ? rows.filter(l => (l.ownerName || "—") === empSel) : rows, [rows, empSel]);

  const { sorted: detailSorted, sortKey, sortDir, toggle } = useSortable(rowsSel, 'requested_amount', 'desc');

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const pick = (k) => setEmpSel(prev => prev === k ? "" : k);
  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", padding: "12px 14px 8px" };
  const selStyle = { padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" };
  const detailCols = ["ownerName", "status", "companyname", "lead_source", "industryname", "state", "requested_amount"];

  return <>
    <SectionHeader title="Leads by Month" actions={[
      <ExportBtn key="ex" onClick={() => exportCSV(detailSorted, detailCols, "leads_by_month.csv")}>Export CSV</ExportBtn>
    ]} />

    {/* slicers — two stacked cards */}
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
      <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…" style={{ ...selStyle, minWidth: 150 }} />
        <select value={sourceSel} onChange={e => setSourceSel(e.target.value)} style={selStyle}><option value="">All Lead Sources</option>{sources.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />
        <select value={statusSel} onChange={e => setStatusSel(e.target.value)} style={selStyle}><option value="">All Statuses</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}</select>
        {empSel && <button onClick={() => setEmpSel("")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>{empSel} <span style={{ fontWeight: 700 }}>✕</span></button>}
        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--color-text-secondary)" }}>{rowsSel.length} leads · {fmt(rowsSel.reduce((s, l) => s + reqAmt(l), 0))} requested</div>
      </Card>
      <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
          Lead Created
          <input type="date" value={created.from} onChange={e => setCreated({ ...created, from: e.target.value })} style={selStyle} />
          <span>–</span>
          <input type="date" value={created.to} onChange={e => setCreated({ ...created, to: e.target.value })} style={selStyle} />
        </label>
      </Card>
    </div>

    <PivotMatrix title="Leads by Sourced By & Month" items={rows} rowKeyOf={l => l.ownerName} dateOf={l => l.createdat} onRowClick={pick} activeRow={empSel} />

    {/* detail */}
    <Card style={{ overflowX: "auto" }}>
      <div style={{ ...cardTitle, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span>Lead Detail</span>
        <ExportBtn onClick={() => exportCSV(detailSorted, detailCols, "lead_detail.csv")}>Export CSV</ExportBtn>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr>
          <STh label="Sourced By"    sortKey="ownerName"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Status"        sortKey="status"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Company Name"  sortKey="companyname"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Lead Source"   sortKey="lead_source"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Industry"      sortKey="industryname"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="State"         sortKey="state"            currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Requested Amt" sortKey="requested_amount" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
        </tr></thead>
        <tbody>{detailSorted.length === 0 ? <tr><Td colSpan={7} style={{ color: "var(--color-text-secondary)" }}>No leads.</Td></tr> : detailSorted.map(l => (
          <tr key={l.id} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td>{l.ownerName || "—"}</Td>
            <Td>{l.status || "—"}</Td>
            <Td style={{ fontWeight: 500 }}>{l.companyname || "—"}</Td>
            <Td>{l.lead_source || "—"}</Td>
            <Td>{l.industryname || "—"}</Td>
            <Td>{l.state || "—"}</Td>
            <TdNum>{fmt(reqAmt(l))}</TdNum>
          </tr>
        ))}</tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={6} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({detailSorted.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailSorted.reduce((s, l) => s + reqAmt(l), 0))}</td>
        </tr></tfoot>
      </table>
    </Card>
  </>;
};
